import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import { useCallback, useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import { Address, useMergeAddress } from 'src/graphql';
import { ConditionInput } from 'src/graphql/types';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { useNavigateRoute, useRouteParams } from 'src/utils/routing';
import { SiteSelector } from './site-selector';

export const MergeSiteModal = () => {
  const { closeModal } = useModalMethods(Modals.MergeSiteModal);
  const { modalProps } = useModalState(Modals.MergeSiteModal);
  const [addressTarget, setAddressTarget] = useState<Address | null>(null);
  const { customViewId } = useRouteParams(UrlParams.customViewId);
  const { mergeAddress, loading } = useMergeAddress();

  const { address = null } = modalProps ?? {};

  const { showErrorAlert, showSuccessAlert } = useNotifications();

  const { navigate } = useNavigateRoute();

  const handleMergeSites = useCallback(async () => {
    if (!address?.addressId) {
      showErrorAlert(`Invalid address target ${address?.addressId}`);
      return;
    }

    if (!addressTarget?.addressId) {
      showErrorAlert(`Invalid address origin ${addressTarget?.addressId}`);
      return;
    }

    const response = await mergeAddress({
      addressOriginId: address.addressId,
      addressTargetId: addressTarget.addressId,
    });

    if (!response) {
      return;
    }

    if (response.errors) {
      showErrorAlert(
        (Array.isArray(response.errors)
          ? (response.errors[0] as { message?: string }).message!
          : (response.errors as { message?: string }).message!) ||
          'Something went wrong merging sites, please try again',
      );
      return;
    }

    showSuccessAlert('Sites were merged');
    navigate(portalPaths.site.full(addressTarget?.addressId), { state: { customViewId } });
    closeModal();
  }, [
    address?.addressId,
    addressTarget?.addressId,
    mergeAddress,
    showSuccessAlert,
    navigate,
    customViewId,
    closeModal,
    showErrorAlert,
  ]);

  const filters = useMemo(() => {
    const filters: ConditionInput[] = [];

    if (!isNullish(address?.partnerId)) {
      filters.push({
        attribute: 'partnerId',
        operator: '=',
        value: String(address.partnerId),
      });
    }

    if (address?.addressId) {
      filters.push({
        attribute: 'addressId',
        operator: '!=',
        value: address.addressId,
      });
    }

    return filters;
  }, [address?.addressId, address?.partnerId]);

  return (
    <Dialog open onClose={closeModal} fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2}>
          <Typography>Merge Site</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <SiteSelector address={address} title="Origin Site" />
          <SiteSelector
            deletable
            filters={filters}
            title="Target Site"
            address={addressTarget}
            setAddress={setAddressTarget}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          loading={!!loading}
          autoFocus
          onClick={handleMergeSites}
          disabled={!!loading || !address || !addressTarget}
        >
          Merge Sites
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
